import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Seo from "../components/Seo";
import { getPrivacyPolicyContent, getTermsContent } from "../lib/api";

const DEFAULT_TERMS_CONTENT = `# 1. Envios y Entregas
- Envio gratis: En compras superiores a $200.000
- Envio estandar: $10.000 en compras menores a $200.000
- Cupon 5%: Por compras superiores a $200.000 recibes un cupon para tu proxima compra

# 2. Cambios y Devoluciones
Cambios:
- Dentro de 2 dias habiles despues de recibir el producto
- Solo por talla (no por referencia)
- El cliente asume costos de envio
- Proceso de cambio tarda 8 a 15 dias habiles
- Compras en descuentos/rebajas no tienen cambio
Devoluciones:
- Solo si el producto llega defectuoso o danado
- Proceso por WhatsApp o correo
- Reembolso hasta 30 dias calendario despues de recibir el producto devuelto
- Se requiere enviar datos bancarios (incluye cedula escaneada)

# 3. Reporte de Danos
Cualquier dano por transporte debe ser reportado dentro de las 48 horas posteriores a la recepcion del pedido.

# 5. Cumplimiento Legal
Eclipse Tenis cumple con la Ley 1581 de 2012 y Decreto 1377 de 2013 sobre proteccion de datos personales en Colombia.
Para mas informacion visite: https://www.sic.gov.co

# 6. Contacto
- WhatsApp: +57 318 421 5616 (Atencion 24/7 todos los dias)
- Email: eclipseestilo20.19@gmail.com
`;

const DEFAULT_PRIVACY_CONTENT = `# Politica de Privacidad
En Eclipse Tenis protegemos tus datos personales y los usamos solo para procesar tus pedidos, enviarte novedades y mejorar tu experiencia.

# 1. Datos que recolectamos
- Nombre y apellido
- Correo electronico
- Telefono / WhatsApp
- Documento de identidad
- Direccion de envio
- Datos del pedido y metodo de pago

# 2. Finalidad del uso de datos
- Procesar pagos y pedidos
- Coordinar despachos y soporte postventa
- Enviar confirmaciones de compra por correo y WhatsApp
- Compartir promociones y novedades (si aceptas suscribirte)

# 3. Comparticion de informacion
Solo compartimos la informacion necesaria con pasarelas de pago y transportadoras para completar tu compra y entrega.

# 4. Seguridad de la informacion
Aplicamos medidas tecnicas y administrativas para proteger tus datos contra acceso no autorizado, perdida o alteracion.

# 5. Derechos del titular
Puedes solicitar actualizacion, correccion o eliminacion de tus datos escribiendo a:
- Email: eclipseestilo20.19@gmail.com
- WhatsApp: +57 318 421 5616

# 6. Base legal en Colombia
Esta politica se rige por la Ley 1581 de 2012 y el Decreto 1377 de 2013.
`;

const parseTermsContent = (content) => {
  const lines = content.split(/\r?\n/);
  const sections = [];
  let current = { title: "", blocks: [] };
  let listItems = [];

  const flushList = () => {
    if (listItems.length) {
      current.blocks.push({ type: "list", items: listItems });
      listItems = [];
    }
  };

  const flushSection = () => {
    if (current.title || current.blocks.length) {
      sections.push(current);
    }
    current = { title: "", blocks: [] };
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }

    if (trimmed.startsWith("#")) {
      flushList();
      flushSection();
      current.title = trimmed.replace(/^#+\s*/, "");
      return;
    }

    if (trimmed.startsWith("- ")) {
      listItems.push(trimmed.slice(2).trim());
      return;
    }

    flushList();
    current.blocks.push({ type: "paragraph", text: trimmed });
  });

  flushList();
  flushSection();
  return sections;
};

const renderInlineLinks = (text) => {
  const regex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  const isLink = /^(https?:\/\/|www\.)/i;
  const parts = text.split(regex).filter(Boolean);
  return parts.map((part, index) => {
    if (!isLink.test(part)) {
      return <span key={index}>{part}</span>;
    }
    const href = part.startsWith("http") ? part : `https://${part}`;
    return (
      <a
        key={index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        {part}
      </a>
    );
  });
};

const Terminos = () => {
  const location = useLocation();
  const isPrivacidad = location.pathname === "/privacidad";
  const seoTitle = isPrivacidad ? "Politica de privacidad" : "Terminos y condiciones";
  const seoDescription = isPrivacidad
    ? "Politica de privacidad y uso de datos personales de Eclipse Tenis."
    : "Terminos y condiciones de compras, envios y devoluciones en Eclipse Tenis.";
  const pageTitle = isPrivacidad ? "Politica de Privacidad" : "Terminos y Condiciones";
  const [termsContent, setTermsContent] = useState(
    isPrivacidad ? DEFAULT_PRIVACY_CONTENT : DEFAULT_TERMS_CONTENT,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    const fetchContent = isPrivacidad ? getPrivacyPolicyContent : getTermsContent;
    const defaultContent = isPrivacidad ? DEFAULT_PRIVACY_CONTENT : DEFAULT_TERMS_CONTENT;
    fetchContent()
      .then((data) => {
        if (!isMounted) return;
        const content = data?.content?.trim();
        if (content) {
          setTermsContent(content);
          return;
        }
        setTermsContent(defaultContent);
      })
      .catch(() => {
        if (isMounted) {
          setTermsContent(defaultContent);
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [isPrivacidad]);

  const sections = useMemo(() => parseTermsContent(termsContent), [termsContent]);

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <Seo title={seoTitle} description={seoDescription} />
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8">{pageTitle}</h1>

        <div className="bg-white rounded-lg p-8 space-y-6">
          {loading && <p className="text-sm text-gray-500">Cargando contenido...</p>}
          {!loading && sections.length === 0 && (
            <p className="text-sm text-gray-500">No hay contenido disponible por el momento.</p>
          )}
          {sections.map((section, index) => (
            <section key={`${section.title}-${index}`}>
              {section.title && <h2 className="text-2xl font-bold mb-4">{section.title}</h2>}
              <div className="space-y-3 text-gray-700">
                {section.blocks.map((block, blockIndex) => {
                  if (block.type === "list") {
                    return (
                      <ul key={`list-${blockIndex}`} className="list-disc pl-6 space-y-1">
                        {block.items.map((item, itemIndex) => (
                          <li key={`item-${itemIndex}`}>{renderInlineLinks(item)}</li>
                        ))}
                      </ul>
                    );
                  }
                  const isLabel = block.text.endsWith(":");
                  return (
                    <p
                      key={`paragraph-${blockIndex}`}
                      className={isLabel ? "font-semibold" : undefined}
                    >
                      {renderInlineLinks(block.text)}
                    </p>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Terminos;
